import React, { FC, useState, useEffect } from 'react';
import Image from 'next/image';
import styles from './ClientBanner.module.scss';
import classNames from 'classnames';
import parse from 'html-react-parser';
// Import lib files
import { bannerProps } from '@/libs/interfaceProps/bannerProps';
import currentDateTimeStamp from '@/libs/currentDateTimestamp/currentDateTimestamp';
import { sanitizeTitle } from '@/libs/sanitizeTitle/sanitizeTitle';
import TruncateReadmore from '@/components/TruncateReadmore/TruncateReadmore';
import { useEnvContext } from '@/context/envContext';
import ClientMultiLogo from '@/components/ClientMultiLogo/ClientMultiLogo';
import { orgBannerProps } from '@/libs/interfaceProps/orgBannerProps';
import { useOrgContext } from '@/context/orgContext';
import { pushGTMClickTags } from '@/libs/pushGTMTags/pushGTMTags';
const ClientBanner: FC<bannerProps> = ({
  orgId,
  t
}) => {
  const {
    cdnEnv: env
  } = useEnvContext();
  const {
    orgData
  } = useOrgContext();
  const [hasHeroBanner, setHasHeroBanner] = useState(true);
  const [titleLineBreakClass, setTitleLineBreakClass] = useState('');

  //  The states to use remove elements from the DOM.
  //  If the given src is not valid.
  const [hasSquareLogo2, setHasSquareLogo2] = useState(false);
  const [hasSquareLogo3, setHasSquareLogo3] = useState(false);
  const [hasSquareLogo4, setHasSquareLogo4] = useState(false);
  const {
    orgName,
    orgDescription,
    orgNumFound,
    orgUrl,
    images
  }: orgBannerProps = orgData;

  // Build the URL for here banner.
  let heroBannerSrc = null;
  if (images?.hero) {
    heroBannerSrc = `${images?.hero}?t=${currentDateTimeStamp()}`;
  }
  useEffect(() => {
    if (heroBannerSrc == null) {
      setHasHeroBanner(false);
    }
    if (images?.logo2) {
      setHasSquareLogo2(true);
    }
    if (images?.logo3) {
      setHasSquareLogo3(true);
    }
    if (images?.logo4) {
      setHasSquareLogo4(true);
    }
  }, [heroBannerSrc, images?.logo2, images?.logo3, images?.logo4]);
  const resourcesIcon = `${env}/rms/images/icons/resources-icon.svg`;
  const orgIcon = `${env}/rms/images/icons/link-icon.svg`;
  const FallbackImage = `${env}/rms/images/company-logo-placeholder.svg`;

  // Build a display name for the client site link
  // function is only called from within the query check for valid orgUrl
  // try/catch  for mallformed strings added on this ap-884 commit based on the ticket originally raised:
  // https://ttgt.atlassian.net/browse/AP-438
  function orgSiteName(url: string) {
    try {
      const orgSiteNameParts = new URL(url);
      // from the parts get the hostname e.g 'www.example.com'
      let orgHostName = orgSiteNameParts.hostname;
      // split the hostname into an array www, example, com
      let orgHostNameParts = orgHostName.replace('www.', '').split('/');
      // rebuild the parts without the 'www' part
      return orgHostNameParts[0];
    } catch (e) {
      // If URL is invalid, it might be missing a protocol.
      let orgHostNameParts = url.replace('https://', '').replace('http://', '').replace('www.', '').split('/');
      return orgHostNameParts[0];
    }
  }
  const fallbackDefault = FallbackImage;

  // Fallback images
  const fallbacks = [`${env}/bitpipe/logos/logo_${orgId}.gif`, fallbackDefault];
  let index = 0;
  const selectFallbackSrc = (event: any) => {
    if (index >= fallbacks.length) return;
    let next = fallbacks[index];
    event.target.src = next;
    index++;
  };
  return <div className={'banners client-banner'} data-bdd={'client-banner'} data-sentry-component="ClientBanner" data-sentry-source-file="ClientBanner.tsx">
   <div className={styles['banner-background-image']}>
    {heroBannerSrc && <Image data-bdd={'hero-banner-image'} src={heroBannerSrc} priority={true} alt={'banner-background'} width={1280} height={212} unoptimized={true} />}
    <div className={classNames(styles['the-client'], hasSquareLogo2 && hasSquareLogo3 && hasSquareLogo4 ? styles['brand-logosx3'] : styles['brand-logos<3'], [hasSquareLogo2, hasSquareLogo3, hasSquareLogo4].includes(true) ? styles['multiple-brand-logos'] : styles['single-client-logo'], hasHeroBanner ? styles['with-hero-banner'] : styles['without-hero-banner'])}>
     <div className={styles['company-logo']} data-bdd={'primary-client-logo'}>
      <Image data-bdd={'bdd-client-logo'} src={`${env}/bitpipe/logos/square1_${orgId}.png?t=${currentDateTimeStamp()}`} alt={'client-logo'} priority={true} width={150} height={150} unoptimized={true} onError={event => {
            selectFallbackSrc(event);
          }} data-sentry-element="Image" data-sentry-source-file="ClientBanner.tsx" />
     </div>

     {[hasSquareLogo2, hasSquareLogo3, hasSquareLogo4].includes(true) && <ClientMultiLogo page="orgpage" setbreakClass={setTitleLineBreakClass} width={150} height={150} />}

     <div className={classNames(styles['client-details'], styles[titleLineBreakClass])}>
      <h2 className={styles['org-title']} data-bdd={'client-banner-heading'} data-testid={'client-banner-heading'}>
       {parse(sanitizeTitle(orgName))}
      </h2>
      <p className={styles['client-details-bottom']}>
       <span className={styles['resource-count']} data-bdd={'resource-count'}>
        <Image src={resourcesIcon} width={'16'} height={'16'} className={styles['icon-center-text']} alt="Resources Found" data-sentry-element="Image" data-sentry-source-file="ClientBanner.tsx" />
        {orgNumFound + ' ' + t('Resources') + ' '}
       </span>
       <span className={styles['client-link']} data-bdd={'client-link'}>
        {orgUrl ? <>
          {' '}
          <Image src={orgIcon} width={'16'} height={'17'} className={styles['icon-center-text']} alt="Org Link" />
          <a className={'text-links-body-color'} href={orgUrl} target="_blank" rel={'nofollow'} onClick={() => {
                  pushGTMClickTags({
                    click_element: 'Anchor',
                    click_section_ID: 'NONE',
                    link_classes: 'client-link',
                    link_id: 'NONE',
                    link_text: orgSiteName(orgUrl),
                    link_url: orgUrl,
                    click_category: 'NONE',
                    click_destination: 'new tab',
                    click_closest_href: 'NONE'
                  });
                }}>
           {' '}
           {orgSiteName(orgUrl)}
          </a>
         </> : ''}
       </span>
      </p>
     </div>
    </div>

    <div className={styles['client-banner-strapline']}>
     <TruncateReadmore classToken={'client-description'} numberOfLines={2} trimBy={'words'} text={sanitizeTitle(orgDescription)} data-sentry-element="TruncateReadmore" data-sentry-source-file="ClientBanner.tsx" />
    </div>
   </div>
  </div>;
};
export default ClientBanner;